import { selectBackgroundTask } from "@/store/background-tasks/background-tasks-selector";
import { useAppSelector } from "@/store/store-hooks";
import { selectHasWritePermission } from "@/store/user-selectors";
import { GUID } from "@faro-lotv/ielement-types";
import { ProgressApiSupportedTaskTypes } from "@faro-lotv/service-wires";
import type { JSX } from "react";
import { AssetDetectionTaskCard } from "./cards/tasks-cards/asset-detection-task-card";
import { BackendFileExportCard } from "./cards/tasks-cards/backend-file-export-card";
import { FloorplanGenerationCard } from "./cards/tasks-cards/floorplan-generation-card";
import { IpxOrderTaskCard } from "./cards/tasks-cards/ipx-order-task-card";
import { ProcessingTaskCard } from "./cards/tasks-cards/processing-task-card";
import { RegistrationTaskCard } from "./cards/tasks-cards/registration-task-card";
import { SnapshotTaskCard } from "./cards/tasks-cards/snapshot-task-card";
import { TiledImageSheetGenerationTaskCard } from "./cards/tasks-cards/tiled-image-sheet-generation-task-card";
import { ToAlignCard } from "./cards/tasks-cards/to-align-card";
import { UploadTaskCard } from "./cards/tasks-cards/upload-task-card";

type ProgressOverviewCardProps = {
  /** The process information to display in the card */
  process: {
    /** ID of the process (either a background task ID or an iElement ID) */
    id: GUID;
    /** True if this is an element that needs to be aligned */
    isElementToAlign?: boolean;
  };
};

/** @returns the progress card to display any process or task */
export function ProgressOverviewCard({
  process,
}: ProgressOverviewCardProps): JSX.Element | null {
  const task = useAppSelector(selectBackgroundTask(process.id));

  const hasWritePermission = useAppSelector(selectHasWritePermission);

  if (process.isElementToAlign && hasWritePermission) {
    return <ToAlignCard iElementID={process.id} />;
  }

  if (!task) {
    return null;
  }

  switch (task.type) {
    case "MultiFileUpload":
    case "FileUpload":
      return <UploadTaskCard task={task} />;
    case "IpxOrder":
      return <IpxOrderTaskCard task={task} />;
    case "TiledImageSheetGeneration":
      return <TiledImageSheetGenerationTaskCard task={task} />;
    case ProgressApiSupportedTaskTypes.pointCloudExport:
    case ProgressApiSupportedTaskTypes.structuredExport:
      return <BackendFileExportCard task={task} isLargeExport />;
    case ProgressApiSupportedTaskTypes.orthophotoGeneration:
      return <BackendFileExportCard task={task} isImageExport />;
    case ProgressApiSupportedTaskTypes.projectReportGeneration:
      return <BackendFileExportCard task={task} isPDFExport />;
    case ProgressApiSupportedTaskTypes.snapshotGeneration:
      return <SnapshotTaskCard task={task} />;
    case ProgressApiSupportedTaskTypes.captur3dFloorplanGeneration:
      return <FloorplanGenerationCard task={task} />;
    case ProgressApiSupportedTaskTypes.c2cRegistration:
    case ProgressApiSupportedTaskTypes.registerMultiCloudDataSet:
    case ProgressApiSupportedTaskTypes.mergePointClouds:
      return <RegistrationTaskCard task={task} />;
    case ProgressApiSupportedTaskTypes.assetDetection:
      return <AssetDetectionTaskCard task={task} />;
    default:
      return <ProcessingTaskCard task={task} />;
  }
}
